import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

const DEBOUNCE_MS = 350;
const BLUR_DISMISS_MS = 150;
const RECENT_MAX = 5;

export type ParsedSearchCommit = {
  mode: 'text' | 'amount';
  value: string;
  searchField?: string;
};

interface SearchInputProps {
  id: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  label?: string;
  /**
   * Overrides the label's classes so a page whose filter labels use a different
   * style can render one label rather than adding a second of its own. Defaults
   * to `text-sm font-medium`, so existing callers are unaffected.
   */
  labelClassName?: string;
  /**
   * The hint shown at the foot of the suggestions dropdown. Defaults to the
   * payments-oriented wording ("name, email, client, or ref"), which is wrong on
   * any page without those fields — pass the fields this search actually covers.
   */
  hint?: string;
  className?: string;
  tooltipRows?: Array<{ label: string; prefix: string; plain: string }>;
  suggestions?: {
    prefixExamples?: string[];
    quickExamples?: string[];
  };
  /** When set, recent searches are persisted in sessionStorage under this key (tab-scoped). */
  sessionStorageKey?: string;
  onParsedSearch?: (parsed: ParsedSearchCommit) => void;
  prefixMap?: Record<string, 'text' | 'amount'>;
}

function isAdvancedSearch(props: SearchInputProps): boolean {
  return Boolean(
    (props.prefixMap && Object.keys(props.prefixMap).length > 0) ||
      props.onParsedSearch ||
      props.sessionStorageKey ||
      (props.suggestions &&
        ((props.suggestions.prefixExamples?.length ?? 0) > 0 ||
          (props.suggestions.quickExamples?.length ?? 0) > 0)) ||
      (props.tooltipRows && props.tooltipRows.length > 0)
  );
}

function parseWithPrefixMap(
  raw: string,
  prefixMap: Record<string, 'text' | 'amount'>
): ParsedSearchCommit {
  const match = raw.match(/^(\w+):(.+)$/);
  if (match) {
    const [, prefix, rest] = match;
    const p = prefix.toLowerCase();
    const mode = prefixMap[p];
    if (mode === 'amount') {
      return { mode: 'amount', value: rest.trim() };
    }
    if (mode === 'text') {
      return { mode: 'text', value: rest.trim(), searchField: p };
    }
  }
  return { mode: 'text', value: raw.trim() };
}

function readRecent(key: string): string[] {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((x): x is string => typeof x === 'string').slice(0, RECENT_MAX);
  } catch {
    return [];
  }
}

function writeRecent(key: string, entries: string[]): void {
  try {
    sessionStorage.setItem(key, JSON.stringify(entries.slice(0, RECENT_MAX)));
  } catch {
    /* ignore */
  }
}

function pushRecent(key: string, query: string): void {
  const q = query.trim();
  if (!q) return;
  const prev = readRecent(key);
  const next = [q, ...prev.filter((e) => e !== q)].slice(0, RECENT_MAX);
  writeRecent(key, next);
}

function removeRecent(key: string, entry: string): void {
  try {
    const prev = readRecent(key);
    writeRecent(
      key,
      prev.filter((e) => e !== entry)
    );
  } catch {
    /* ignore */
  }
}

const SearchInputSimple: React.FC<SearchInputProps> = ({
  id,
  value,
  onChange,
  placeholder = 'Search...',
  label = 'Search',
  labelClassName,
  className,
}) => (
  <div className="flex flex-col gap-2">
    <label htmlFor={id} className={labelClassName ?? 'text-sm font-medium'}>
      {label}
    </label>
    <Input
      id={id}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className={className || 'w-[240px]'}
    />
  </div>
);

const SearchInputAdvanced: React.FC<SearchInputProps> = ({
  id,
  value,
  onChange,
  placeholder = 'Search...',
  label = 'Search',
  labelClassName,
  hint,
  className,
  tooltipRows,
  suggestions,
  sessionStorageKey,
  onParsedSearch,
  prefixMap,
}) => {
  const [draft, setDraft] = useState(value);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [recent, setRecent] = useState<string[]>(() =>
    sessionStorageKey ? readRecent(sessionStorageKey) : []
  );
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const blurTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    setDraft(value);
  }, [value]);

  const clearDebounce = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
  }, []);

  const emitCommit = useCallback(
    (raw: string, options?: { immediate?: boolean; bypassRecent?: boolean }) => {
      const trimmed = raw.trim();
      const len = trimmed.length;

      if (len === 0) {
        clearDebounce();
        onParsedSearch?.({ mode: 'text', value: '' });
        onChange('');
        if (sessionStorageKey && !options?.bypassRecent) {
          setRecent(readRecent(sessionStorageKey));
        }
        return;
      }

      if (len === 1 && !options?.immediate) {
        return;
      }

      const run = () => {
        let parsed: ParsedSearchCommit;
        if (prefixMap && Object.keys(prefixMap).length > 0) {
          parsed = parseWithPrefixMap(trimmed, prefixMap);
        } else {
          parsed = { mode: 'text', value: trimmed };
        }
        onParsedSearch?.(parsed);
        onChange(parsed.mode === 'text' || parsed.mode === 'amount' ? parsed.value : trimmed);
        if (sessionStorageKey && trimmed) {
          pushRecent(sessionStorageKey, trimmed);
          setRecent(readRecent(sessionStorageKey));
        }
      };

      if (options?.immediate) {
        clearDebounce();
        run();
        return;
      }

      clearDebounce();
      debounceRef.current = setTimeout(() => {
        debounceRef.current = null;
        run();
      }, DEBOUNCE_MS);
    },
    [clearDebounce, onChange, onParsedSearch, prefixMap, sessionStorageKey]
  );

  useEffect(() => {
    return () => {
      clearDebounce();
      if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current);
    };
  }, [clearDebounce]);

  const handleDraftChange = (next: string) => {
    setDraft(next);
    setShowSuggestions(false);
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }

    const trimmed = next.trim();
    if (trimmed.length <= 1) {
      clearDebounce();
      onParsedSearch?.({ mode: 'text', value: '' });
      onChange('');
      return;
    }
    emitCommit(next);
  };

  const handleFocus = () => {
    if (draft.trim() === '') {
      if (sessionStorageKey) {
        try {
          setRecent(readRecent(sessionStorageKey));
        } catch {
          setRecent([]);
        }
      }
      setShowSuggestions(Boolean(suggestions || sessionStorageKey));
    }
  };

  const handleBlur = () => {
    blurTimeoutRef.current = setTimeout(() => {
      setShowSuggestions(false);
      blurTimeoutRef.current = null;
    }, BLUR_DISMISS_MS);
  };

  const applySuggestion = (text: string) => {
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }
    setDraft(text);
    setShowSuggestions(false);
    emitCommit(text, { immediate: true });
  };

  const handleClear = () => {
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }
    clearDebounce();
    setDraft('');
    setShowSuggestions(false);
    onParsedSearch?.({ mode: 'text', value: '' });
    onChange('');
  };

  const hasSuggestionsUi =
    (sessionStorageKey && recent.length > 0) ||
    (suggestions?.prefixExamples?.length ?? 0) > 0 ||
    (suggestions?.quickExamples?.length ?? 0) > 0;

  return (
    <div className="flex flex-col gap-2">
      <label htmlFor={id} className={labelClassName ?? 'text-sm font-medium'}>
        {label}
      </label>
      <div className="relative flex items-start gap-1">
        <div className="relative z-20 min-w-[240px]">
          <Input
            id={id}
            role="combobox"
            aria-expanded={showSuggestions}
            aria-controls={`${id}-suggestions`}
            value={draft}
            onChange={(e) => handleDraftChange(e.target.value)}
            onFocus={handleFocus}
            onBlur={handleBlur}
            placeholder={placeholder}
            className={cn(className || 'w-[240px]', 'pr-16')}
            autoComplete="off"
          />
          {draft.length > 0 && (
            <button
              type="button"
              aria-label="Clear search"
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded px-1 text-muted-foreground hover:text-foreground"
              onMouseDown={(e) => e.preventDefault()}
              onClick={handleClear}
            >
              ✕
            </button>
          )}
          {showSuggestions && hasSuggestionsUi && (
            // biome-ignore lint/a11y/useSemanticElements: custom suggestions panel; ticket requires listbox semantics
            <div
              id={`${id}-suggestions`}
              role="listbox"
              className="absolute left-0 top-full z-30 mt-1 w-max min-w-[260px] max-w-[min(100vw-2rem,420px)] rounded-md border bg-popover p-2 text-sm shadow-md"
              onMouseDown={(e) => e.preventDefault()}
            >
              {sessionStorageKey && recent.length > 0 && (
                <div className="mb-2">
                  <div className="text-muted-foreground mb-1 text-xs">── Recent searches ──</div>
                  {recent.map((r) => (
                    <div
                      key={r}
                      className="flex items-center gap-1 rounded px-1 py-1 hover:bg-muted"
                    >
                      {/* biome-ignore lint/a11y/useSemanticElements: option inside listbox per spec */}
                      <button
                        type="button"
                        role="option"
                        className="flex min-w-0 flex-1 items-center gap-2 text-left"
                        onClick={() => applySuggestion(r)}
                      >
                        <span aria-hidden>🕐</span>
                        <span className="truncate">{r}</span>
                      </button>
                      <button
                        type="button"
                        aria-label={`Remove ${r}`}
                        className="shrink-0 rounded px-1 text-muted-foreground hover:text-foreground"
                        onClick={() => {
                          if (sessionStorageKey) removeRecent(sessionStorageKey, r);
                          setRecent(sessionStorageKey ? readRecent(sessionStorageKey) : []);
                        }}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}
              {(suggestions?.prefixExamples?.length ?? 0) > 0 && (
                <div className="mb-2">
                  <div className="text-muted-foreground mb-1 text-xs">── Prefix search ──</div>
                  {(suggestions?.prefixExamples ?? []).map((ex) => (
                    // biome-ignore lint/a11y/useSemanticElements: option inside listbox per spec
                    <button
                      key={ex}
                      type="button"
                      role="option"
                      className="flex w-full cursor-pointer items-center gap-2 rounded px-1 py-1 text-left hover:bg-muted"
                      onClick={() => applySuggestion(ex)}
                    >
                      <span aria-hidden>🔍</span>
                      <span className="font-mono text-xs">{ex}</span>
                    </button>
                  ))}
                </div>
              )}
              {(suggestions?.quickExamples?.length ?? 0) > 0 && (
                <div className="mb-2">
                  <div className="text-muted-foreground mb-1 text-xs">── Quick search ──</div>
                  {(suggestions?.quickExamples ?? []).map((ex) => (
                    // biome-ignore lint/a11y/useSemanticElements: option inside listbox per spec
                    <button
                      key={ex}
                      type="button"
                      role="option"
                      className="flex w-full cursor-pointer items-center gap-2 rounded px-1 py-1 text-left hover:bg-muted"
                      onClick={() => applySuggestion(ex)}
                    >
                      <span aria-hidden>🔍</span>
                      <span>{ex}</span>
                    </button>
                  ))}
                </div>
              )}
              <div className="text-muted-foreground border-t pt-2 text-xs">
                {hint
                  ? `💡 ${hint}`
                  : (suggestions?.prefixExamples?.length ?? 0) > 0
                    ? '💡 Try name:, client:, email:, or ref: for faster, targeted results.'
                    : '💡 Search by name, email, client, or ref'}
              </div>
            </div>
          )}
        </div>
        {tooltipRows && tooltipRows.length > 0 && (
          <div className="relative shrink-0 self-center">
            <button
              type="button"
              aria-label="Search help"
              onMouseEnter={() => setTooltipOpen(true)}
              onMouseLeave={() => setTooltipOpen(false)}
              className="text-muted-foreground hover:text-foreground rounded px-1 text-sm leading-none"
            >
              ⓘ
            </button>
            {tooltipOpen && (
              <div
                role="tooltip"
                className="absolute bottom-full right-0 z-40 mb-1 min-w-[280px] whitespace-nowrap rounded-md border bg-popover p-3 text-xs shadow-md"
              >
                <div className="mb-2 font-medium">Search by:</div>
                <ul className="mb-2 space-y-1">
                  {tooltipRows.map((row) => (
                    <li key={row.label}>
                      • {row.label} → <span className="font-mono">{row.prefix}</span> /{' '}
                      <span className="font-mono">{row.plain}</span>
                    </li>
                  ))}
                </ul>
                <div className="border-t pt-2 text-muted-foreground">
                  💡 Tip: type fieldname:value for targeted search
                  <br />
                  <span className="font-mono">e.g. name:Apple LLP</span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export const SearchInput: React.FC<SearchInputProps> = (props) => {
  if (isAdvancedSearch(props)) {
    return <SearchInputAdvanced {...props} />;
  }
  return <SearchInputSimple {...props} />;
};
